import React, { useState, useEffect, useRef } from 'react';
import { string } from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from '../../util/reactIntl';
import css from './NotificationBell.module.css';

/**
 * NotificationBell — in-app notification center with dropdown.
 *
 * Polls /api/notifications/mine every 60 seconds.
 * Shows unread count badge, dropdown with notifications list.
 *
 * Usage (in TopbarDesktop):
 *   {features.inAppNotifications && isAuthenticated && (
 *     <NotificationBell />
 *   )}
 */
const NotificationBell = ({ className }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const dropdownRef = useRef(null);

  const fetchNotifications = async () => {
    try {
      const res = await fetch('/api/notifications/mine');
      if (!res.ok) return;
      const data = await res.json();
      setNotifications(data.notifications || []);
      setUnreadCount(data.unreadCount || 0);
    } catch (_) {}
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, []);

  // Close on outside click
  useEffect(() => {
    const handleClick = e => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleOpen = async () => {
    setIsOpen(prev => !prev);
    if (!isOpen && unreadCount > 0) {
      try {
        await fetch('/api/notifications/read-all', { method: 'POST' });
        setUnreadCount(0);
        setNotifications(prev => prev.map(n => ({ ...n, readAt: n.readAt || new Date().toISOString() })));
      } catch (_) {}
    }
  };

  const handleItemClick = async (n) => {
    if (n.url) window.location.href = n.url;
  };

  return (
    <div className={classNames(css.root, className)} ref={dropdownRef}>
      <button type="button" onClick={handleOpen} className={css.bell}>
        <svg viewBox="0 0 24 24" fill="currentColor" className={css.icon}>
          <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6V11c0-3.07-1.63-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"/>
        </svg>
        {unreadCount > 0 && (
          <span className={css.badge}>{unreadCount > 9 ? '9+' : unreadCount}</span>
        )}
      </button>

      {isOpen && (
        <div className={css.dropdown}>
          <div className={css.header}>
            <span className={css.headerTitle}>
              <FormattedMessage id="NotificationBell.title" />
            </span>
          </div>
          <div className={css.list}>
            {notifications.length === 0 ? (
              <p className={css.empty}>
                <FormattedMessage id="NotificationBell.empty" />
              </p>
            ) : (
              notifications.slice(0, 10).map(n => (
                <div
                  key={n.id}
                  className={classNames(css.item, !n.readAt && css.unread)}
                  onClick={() => handleItemClick(n)}
                  role={n.url ? 'link' : 'listitem'}
                >
                  <p className={css.itemTitle}>{n.title}</p>
                  {n.body && <p className={css.itemBody}>{n.body}</p>}
                  <p className={css.itemTime}>
                    {new Date(n.createdAt).toLocaleDateString()}
                  </p>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

NotificationBell.defaultProps = { className: null };
NotificationBell.propTypes = { className: string };

export default NotificationBell;
